import clsx from 'clsx';
import React, {useEffect, useMemo, useState} from 'react';
import {designSystemToken, Icon} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {SearchType} from '@/types/models/creator-crm/creatorCrm';
import AutocompleteInput, {Option} from '@/components/autocomplete-input';
import SegmentedControl from '@/components/segmented-control';
import styles from './UnifiedSearchInput.module.scss';

export type UnifiedSearchInputProps = {
  className?: string;
  searchType: SearchType;
  onSearchTypeChange: (type: SearchType) => void;
  chips: string[];
  onChange: (searchType: SearchType, chips: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
};

const SEARCH_TYPE_OPTIONS: Array<{id: SearchType; labelLocaleKey: string}> = [
  {id: 'all', labelLocaleKey: 'All'},
  {id: 'content', labelLocaleKey: 'Content'},
  {id: 'name', labelLocaleKey: 'Name'},
  {id: 'handle', labelLocaleKey: 'Handle'},
  {id: 'bio', labelLocaleKey: 'Bio'},
];

const isValidChip = (chip: string | null | undefined): boolean => {
  return Boolean(chip && chip.trim().length > 0);
};

/** Name and Handle accept a single term; other modes accept multiple chips. */
const isSingleTermSearchType = (type: SearchType): boolean =>
  type === 'name' || type === 'handle';

function UnifiedSearchInput(props: UnifiedSearchInputProps) {
  const {
    className,
    searchType,
    onSearchTypeChange,
    chips,
    onChange,
    placeholder,
    disabled,
  } = props;

  const [inputValue, setInputValue] = useState('');
  const [localSearchType, setLocalSearchType] = useState(searchType);

  useEffect(() => {
    setLocalSearchType(searchType);
  }, [searchType]);

  const isSingleTermMode = isSingleTermSearchType(localSearchType);
  const isMultiple = !isSingleTermMode;

  const selectedOptions: Option[] = useMemo(() => {
    const validChips = chips.filter(isValidChip);
    if (isSingleTermMode) {
      if (validChips.length === 0) {
        return [];
      }
      return [{id: validChips[0], label: validChips[0]}];
    }
    return validChips.map((chip) => ({id: chip, label: chip}));
  }, [chips, isSingleTermMode]);

  const segments = useMemo(
    () =>
      SEARCH_TYPE_OPTIONS.map((option) => ({
        id: option.id,
        label: translate(option.labelLocaleKey),
      })),
    []
  );

  useEffect(() => {
    if (isSingleTermMode) {
      const validChip = chips.find(isValidChip);
      setInputValue(validChip ?? '');
    }
  }, [isSingleTermMode, chips]);

  const createChips = (
    input: string,
    existingChips: string[],
    isSingleTerm: boolean
  ): string[] => {
    const trimmed = input.trim();
    if (!trimmed) {
      return existingChips.filter(isValidChip);
    }
    if (isSingleTerm) {
      return [trimmed];
    }
    const validExistingChips = existingChips.filter(isValidChip);
    const allChips = [...validExistingChips, trimmed];
    return Array.from(new Set(allChips));
  };

  const submitChips = (newChips: string[]) => {
    if (newChips.length > 0) {
      onChange(localSearchType, newChips);
    }
    if (!isSingleTermMode) {
      setInputValue('');
    }
  };

  const normalizeChipValue = (val: string | Option): string => {
    if (typeof val === 'string') {
      return val.trim();
    }
    return val?.label?.trim() ?? '';
  };

  const handleChipChange = (
    _: React.SyntheticEvent,
    value: (string | Option)[] | string | Option | null,
    reason: string
  ) => {
    if (isSingleTermMode) {
      const singleValue = value as Option | null;
      const normalized = singleValue ? normalizeChipValue(singleValue) : '';
      const newChips = normalized ? [normalized] : [];
      onChange(localSearchType, newChips);
      setInputValue(singleValue?.label ?? '');
      return;
    }

    if (reason !== 'removeOption') {
      setInputValue('');
    }
    const valueArray = (value as (string | Option)[]) || [];
    const newChips = Array.from(
      new Set(valueArray.map(normalizeChipValue).filter(isValidChip))
    );
    onChange(localSearchType, newChips);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      if (isSingleTermMode && chips.length > 0) {
        return;
      }
      event.preventDefault();
      const newChips = createChips(inputValue, chips, isSingleTermMode);
      if (newChips.length > chips.length) {
        onChange(localSearchType, newChips);
      }
      if (!isSingleTermMode) {
        setInputValue('');
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const newChips = createChips(inputValue, chips, isSingleTermMode);
      submitChips(newChips);
    }
  };

  const handleOnBlur = () => {
    const newChips = createChips(inputValue, chips, isSingleTermMode);
    const hasInputValue = inputValue.trim().length > 0;
    if (hasInputValue || newChips.length > 0) {
      onChange(localSearchType, newChips);
    }
  };

  const handleSearchTypeChange = (type: SearchType) => {
    setLocalSearchType(type);
    setInputValue('');
    onSearchTypeChange(type);

    if (chips.length > 0) {
      onChange(type, []);
    }
  };

  return (
    <div className={clsx(styles.root, className)}>
      <div className={styles.container}>
        <div className={styles.searchIcon}>
          <Icon
            appearance="neutral"
            size="small"
            name="Actions-Search-Generic"
            color={designSystemToken('semantic.fg.inverse-secondary')}
          />
        </div>
        <div className={styles.inputSection}>
          <div
            className={clsx(
              styles.inputWrapper,
              isMultiple && styles.inputWrapperMultiple
            )}
          >
            <AutocompleteInput
              testID="unified-search-input"
              multiple={isMultiple}
              placeholder={placeholder}
              options={[]}
              selectedOptions={
                isMultiple ? selectedOptions : selectedOptions[0] || null
              }
              inputValue={inputValue}
              onInputChange={handleInputChange}
              onChange={handleChipChange}
              onKeyDown={handleKeyDown}
              onBlur={handleOnBlur}
              disabled={disabled}
              minHeight={24}
              minWidth={0}
              clearIcon={isMultiple ? false : undefined}
              chipSize="small"
            />
          </div>
        </div>
      </div>
      <div className={styles.segmentedControlWrapper}>
        <SegmentedControl
          testID="unified-search-type-selector"
          segments={segments}
          selectedSegmentId={localSearchType}
          onChange={(segment) => handleSearchTypeChange(segment as SearchType)}
          dynamicHighlightWidth
          classes={{
            control: styles.segmentedControl,
            segment: styles.segmentedControlSegment,
            activeSegment: styles.segmentedControlActiveSegment,
          }}
        />
      </div>
    </div>
  );
}

export default UnifiedSearchInput;
